import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { parseISO, format, subMonths, startOfMonth, isAfter, isSameMonth } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import { bookingsAPI } from '../api/bookings';
import { providersAPI } from '../api/providers';
import Spinner from '../components/common/Spinner';
import { formatCurrency } from '../utils/formatter';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  StarIcon,
  ArrowRightIcon,
  ClockIcon,
  ChartBarIcon,
  ScissorsIcon,
  UserCircleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

const UPCOMING_STATUSES = new Set(['PENDING', 'CONFIRMED', 'ACCEPTED', 'PAID']);
const COMPLETED_STATUSES = new Set(['COMPLETED']);
const CANCELLED_STATUSES = new Set(['CANCELLED', 'DECLINED', 'REFUNDED', 'NO_SHOW']);

const STATUS_COLORS = {
  Completed: '#22c55e',
  Upcoming: 'var(--orange-9)',
  Cancelled: '#e5484d',
};

const bookingAmount = (b) => Number(b.totalPaid || b.subtotal || b.totalPrice || b.price || 0);
const bookingDate = (b) => {
  const raw = b.startAt || b.scheduledAt || b.date || b.createdAt;
  if (!raw) return null;
  try { return typeof raw === 'string' ? parseISO(raw) : new Date(raw); }
  catch { return null; }
};

const Card = ({ children, style, ...rest }) => (
  <div
    {...rest}
    className={`dashboard-card min-w-0 ${rest.className || ''}`}
    style={{
      backgroundColor: '#fff',
      border: '1px solid var(--gray-4)',
      borderRadius: '14px',
      padding: 'clamp(14px, 4vw, 20px)',
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, children, action }) => (
  <div className="dashboard-section-title flex min-w-0 items-start justify-between gap-3 mb-4">
    <div className="flex min-w-0 items-center gap-2">
      {Icon && <Icon className="dashboard-section-title__icon" style={{ width: '18px', height: '18px', color: 'var(--orange-9)' }} />}
      <h3
        className="text-wrap-safe"
        style={{
          fontSize: 'var(--font-size-small)',
          fontWeight: 600,
          color: 'var(--gray-12)',
          margin: 0,
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {children}
      </h3>
    </div>
    {action}
  </div>
);

const StatCard = ({ label, value, sub, icon: Icon, tint, iconColor }) => (
  <Card className="dashboard-stat-card" style={{ padding: 'clamp(10px, 2.5vw, 14px)' }}>
    <div className="dashboard-stat-card__top flex min-w-0 items-center gap-2 sm:gap-3 mb-1.5 sm:mb-3">
      <div
        className="dashboard-stat-card__icon"
        style={{
          width: 'clamp(26px, 6vw, 32px)',
          height: 'clamp(26px, 6vw, 32px)',
          borderRadius: '8px',
          backgroundColor: tint,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon className="dashboard-stat-card__icon-svg" style={{ width: 'clamp(13px, 3.2vw, 16px)', height: 'clamp(13px, 3.2vw, 16px)', color: iconColor }} />
      </div>
      <span className="dashboard-stat-card__label text-wrap-safe" style={{ fontSize: 'clamp(11px, 2.8vw, 12px)', color: 'var(--gray-10)' }}>{label}</span>
    </div>
    <p
      className="dashboard-stat-card__value text-wrap-safe"
      style={{
        fontSize: 'clamp(17px, 4.5vw, 21px)',
        fontWeight: 600,
        color: 'var(--gray-12)',
        margin: 0,
        textAlign: 'left',
        fontFamily: "'Poppins', sans-serif",
        lineHeight: 1.1,
      }}
    >
      {value}
    </p>
    {sub && (
      <p className="dashboard-stat-card__sub text-wrap-safe" style={{ fontSize: 'clamp(10px, 2.6vw, 11px)', color: 'var(--gray-10)', margin: '3px 0 0', textAlign: 'left' }}>
        {sub}
      </p>
    )}
  </Card>
);

// SVG bar chart: monthly spend
const SpendChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = 520;
  const height = 180;
  const padX = 28;
  const padTop = 16;
  const padBottom = 36;
  const usableW = width - padX * 2;
  const usableH = height - padTop - padBottom;
  const barW = usableW / data.length - 14;

  return (
    <svg className="dashboard-spend-chart" viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* gridlines */}
      {[0.25, 0.5, 0.75, 1].map((t) => {
        const y = padTop + usableH * (1 - t);
        return (
          <line
            key={t}
            x1={padX}
            x2={width - padX}
            y1={y}
            y2={y}
            stroke="var(--gray-4)"
            strokeDasharray="3 3"
          />
        );
      })}
      {data.map((d, i) => {
        const h = (d.value / max) * usableH;
        const x = padX + i * (usableW / data.length) + 7;
        const y = padTop + usableH - h;
        return (
          <g key={d.label}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={6}
              fill="var(--orange-9)"
              opacity={d.value === 0 ? 0.15 : 0.95}
            >
              <title>{`${d.label}: ${formatCurrency(d.value)}`}</title>
            </rect>
            <text
              x={x + barW / 2}
              y={height - padBottom + 18}
              textAnchor="middle"
              fontSize="11"
              fill="var(--gray-10)"
              fontFamily="'Poppins', sans-serif"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// SVG donut chart
const Donut = ({ segments, total, centerLabel = 'Total' }) => {
  const size = 160;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="dashboard-donut" style={{ position: 'relative', width: 'var(--dashboard-donut-size, 160px)', height: 'var(--dashboard-donut-size, 160px)' }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gray-4)" strokeWidth={stroke} />
        {segments.map((s, i) => {
          const frac = total > 0 ? s.value / total : 0;
          const len = frac * c;
          const circle = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
          );
          offset += len;
          return circle;
        })}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          className="dashboard-donut__value"
          style={{
            fontSize: '26px',
            fontWeight: 700,
            color: 'var(--gray-12)',
            fontFamily: "'Poppins', sans-serif",
            lineHeight: 1,
          }}
        >
          {total}
        </span>
        <span className="dashboard-donut__label" style={{ fontSize: '11px', color: 'var(--gray-10)', marginTop: '2px' }}>{centerLabel}</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [providerMap, setProviderMap] = useState({});
  const [serviceNames, setServiceNames] = useState({});

  const resolveServiceName = (b) =>
    serviceNames[b.serviceId] || b.service?.name || b.serviceName || 'Service';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [bookingsData, reviewsData] = await Promise.all([
          bookingsAPI.getBookings({ clientId: user?.id }, { silent: true }).catch(() => ({ bookings: [] })),
          providersAPI.getMyReviews().catch(() => ({ reviews: [] })),
        ]);
        if (cancelled) return;

        const list = Array.isArray(bookingsData) ? bookingsData : bookingsData?.bookings || [];
        setBookings(list);
        setReviews(reviewsData?.reviews || []);

        const providerIds = [...new Set(list.map((b) => b.providerId).filter(Boolean))];
        const pairs = await Promise.all(
          providerIds.map((id) =>
            Promise.all([
              providersAPI.getProvider(id).catch(() => null),
              providersAPI.getProviderServices(id).catch(() => null),
            ])
          )
        );
        if (cancelled) return;
        const map = {};
        const svcMap = {};
        providerIds.forEach((id, i) => {
          const [profile, svcData] = pairs[i];
          if (profile) map[id] = profile;
          const services = svcData?.services || (Array.isArray(svcData) ? svcData : []);
          services.forEach((s) => { if (s?.id) svcMap[s.id] = s.name; });
        });
        setProviderMap(map);
        setServiceNames(svcMap);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (user?.id) load();
    return () => { cancelled = true; };
  }, [user?.id]);

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);

    const upcoming = bookings.filter((b) => {
      if (!UPCOMING_STATUSES.has(b.status)) return false;
      const d = bookingDate(b);
      return d ? isAfter(d, now) || isSameMonth(d, now) : true;
    });

    const completed = bookings.filter((b) => COMPLETED_STATUSES.has(b.status));
    const cancelled = bookings.filter((b) => CANCELLED_STATUSES.has(b.status));

    const totalSpent = completed.reduce((acc, b) => acc + bookingAmount(b), 0);
    const monthSpent = completed.reduce((acc, b) => {
      const d = bookingDate(b);
      return d && isAfter(d, monthStart) ? acc + bookingAmount(b) : acc;
    }, 0);

    const avgReviewRating =
      reviews.length > 0
        ? reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length
        : 0;

    // next upcoming appointment
    const upcomingSorted = [...upcoming]
      .map((b) => ({ b, d: bookingDate(b) }))
      .filter((x) => x.d && isAfter(x.d, now))
      .sort((a, b) => a.d - b.d);
    const nextAppt = upcomingSorted[0]?.b || null;

    // monthly spend (last 6 months)
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const m = subMonths(monthStart, i);
      months.push({ key: format(m, 'yyyy-MM'), label: format(m, 'MMM'), value: 0 });
    }
    completed.forEach((b) => {
      const d = bookingDate(b);
      if (!d) return;
      const key = format(d, 'yyyy-MM');
      const slot = months.find((m) => m.key === key);
      if (slot) slot.value += bookingAmount(b);
    });

    // top services
    const serviceCounts = {};
    bookings.forEach((b) => {
      const name = resolveServiceName(b);
      if (!name) return;
      serviceCounts[name] = (serviceCounts[name] || 0) + 1;
    });
    const topServices = Object.entries(serviceCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      upcoming,
      completed,
      cancelled,
      totalSpent,
      monthSpent,
      avgReviewRating,
      nextAppt,
      months,
      topServices,
    };
  }, [bookings, reviews, serviceNames]);

  const donutSegments = useMemo(
    () => [
      { label: 'Completed', value: stats.completed.length, color: STATUS_COLORS.Completed },
      { label: 'Upcoming', value: stats.upcoming.length, color: 'var(--orange-9)' },
      { label: 'Cancelled', value: stats.cancelled.length, color: STATUS_COLORS.Cancelled },
    ],
    [stats]
  );
  const donutTotal = donutSegments.reduce((a, s) => a + s.value, 0);

  const recentActivity = useMemo(
    () =>
      [...bookings]
        .map((b) => ({ b, d: bookingDate(b) }))
        .filter((x) => x.d)
        .sort((a, b) => b.d - a.d)
        .slice(0, 5)
        .map((x) => x.b),
    [bookings]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  const nextApptProvider = stats.nextAppt ? providerMap[stats.nextAppt.providerId] : null;

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="dashboard-content w-full min-w-0 px-4 py-5 sm:px-6 sm:py-8 lg:px-8">

        {/* Header */}
        <div className="dashboard-header mb-8 text-center">
          <p className="type-small text-wrap-safe mx-auto max-w-[320px] sm:max-w-none" style={{ color: 'var(--gray-10)', margin: 0, textAlign: 'center' }}>
            Here's a snapshot of your beauty journey so far.
          </p>
        </div>

        {/* Stats grid */}
        <div className="dashboard-stats-grid grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-4 mb-4 sm:mb-6">
          <StatCard
            label="Upcoming"
            value={stats.upcoming.length}
            sub={stats.upcoming.length === 0 ? 'No appointments scheduled' : 'Bookings ahead'}
            icon={CalendarDaysIcon}
            tint="var(--orange-3)"
            iconColor="var(--orange-9)"
          />
          <StatCard
            label="Completed"
            value={stats.completed.length}
            sub={`${stats.cancelled.length} cancelled`}
            icon={CheckCircleIcon}
            tint="#dcfce7"
            iconColor="#22c55e"
          />
          <StatCard
            label="Total Spent"
            value={formatCurrency(stats.totalSpent)}
            sub={`${formatCurrency(stats.monthSpent)} this month`}
            icon={CurrencyDollarIcon}
            tint="#dbeafe"
            iconColor="#3b82f6"
          />
          <StatCard
            label="Reviews Given"
            value={reviews.length}
            sub={
              reviews.length > 0
                ? `Avg ${stats.avgReviewRating.toFixed(1)} ★ rating`
                : 'Share your experience'
            }
            icon={StarIcon}
            tint="#fef9c3"
            iconColor="#eab308"
          />
        </div>

        {/* Next appointment — compact one-liner */}
        {stats.nextAppt && (
          <Link
            to="/bookings"
            className="flex items-center gap-2.5 mb-6"
            style={{
              background: 'linear-gradient(135deg, var(--orange-2), #fff)',
              border: '1px solid var(--orange-5)',
              borderRadius: '12px',
              padding: '10px 14px',
              textDecoration: 'none',
            }}
          >
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                backgroundColor: 'var(--orange-9)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarDaysIcon style={{ width: '15px', height: '15px' }} />
            </div>
            <p
              className="flex-1 min-w-0 truncate"
              style={{ fontSize: 'clamp(12px, 3vw, 14px)', color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}
            >
              <span style={{ fontWeight: 700, color: 'var(--orange-10)' }}>Next: </span>
              <span style={{ fontWeight: 600 }}>{resolveServiceName(stats.nextAppt)}</span>
              {nextApptProvider?.businessName && (
                <span style={{ color: 'var(--gray-11)' }}> with {nextApptProvider.businessName}</span>
              )}
              {bookingDate(stats.nextAppt) && (
                <span style={{ color: 'var(--gray-11)' }}>
                  {' '}· {format(bookingDate(stats.nextAppt), 'EEE, MMM d')}, {format(bookingDate(stats.nextAppt), 'p')}
                </span>
              )}
            </p>
            <ArrowRightIcon style={{ width: '15px', height: '15px', color: 'var(--orange-10)', flexShrink: 0 }} />
          </Link>
        )}

        {/* Charts row */}
        <div className="dashboard-chart-grid grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Monthly spend */}
          <Card style={{ gridColumn: 'span 1 / span 1' }} className="lg:col-span-2">
            <SectionTitle icon={ArrowTrendingUpIcon}>Monthly Spending</SectionTitle>
            {stats.months.some((m) => m.value > 0) ? (
              <SpendChart data={stats.months} />
            ) : (
              <div
                style={{
                  height: '180px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--gray-10)',
                  fontSize: '13px',
                }}
              >
                No spending activity yet — your first booking will show here.
              </div>
            )}
          </Card>

          {/* Donut */}
          <Card>
            <SectionTitle icon={ChartBarIcon}>Bookings Breakdown</SectionTitle>
            <div className="dashboard-bookings-breakdown flex min-w-0 flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-5">
              <Donut segments={donutSegments} total={donutTotal} centerLabel="Bookings" />
              <ul className="dashboard-donut-legend w-full sm:w-auto" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {donutSegments.map((s) => (
                  <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: s.color,
                        display: 'inline-block',
                      }}
                    />
                    <div>
                      <div className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', color: 'var(--gray-12)', fontWeight: 600, textAlign: 'left' }}>
                        {s.label}
                      </div>
                      <div className="text-wrap-safe" style={{ fontSize: 'var(--font-size-caption)', color: 'var(--gray-10)', textAlign: 'left' }}>
                        {s.value} ({donutTotal > 0 ? Math.round((s.value / donutTotal) * 100) : 0}%)
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>

        {/* Services + Activity row */}
        <div className="dashboard-services-grid grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top services */}
          <Card>
            <SectionTitle icon={ScissorsIcon}>Your Top Services</SectionTitle>
            {stats.topServices.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--gray-10)', padding: '8px 0' }}>
                No bookings yet.
              </div>
            ) : (
              <ul className="dashboard-top-services-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {stats.topServices.map((s) => {
                  const pct = Math.round((s.count / stats.topServices[0].count) * 100);
                  return (
                    <li key={s.name}>
                      <div className="dashboard-top-services-row flex min-w-0 items-start justify-between gap-3" style={{ marginBottom: '6px' }}>
                        <span className="dashboard-top-services-name text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', fontWeight: 500, color: 'var(--gray-12)' }}>
                          {s.name}
                        </span>
                        <span className="dashboard-top-services-count shrink-0" style={{ fontSize: 'var(--font-size-caption)', color: 'var(--gray-10)' }}>
                          {s.count} booking{s.count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div
                        className="dashboard-top-services-bar"
                        style={{
                          height: '6px',
                          borderRadius: '3px',
                          backgroundColor: 'var(--gray-3)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            backgroundColor: 'var(--orange-9)',
                            transition: 'width 0.6s ease',
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Recent activity */}
          <Card>
            <SectionTitle
              icon={ClockIcon}
              action={
                <Link
                  to="/bookings"
                  style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none' }}
                >
                  View all
                </Link>
              }
            >
              Recent Activity
            </SectionTitle>
            {recentActivity.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--gray-10)', padding: '8px 0' }}>
                No recent activity.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' }}>
                {recentActivity.map((b, i) => {
                  const d = bookingDate(b);
                  const provider = providerMap[b.providerId];
                  const color =
                    COMPLETED_STATUSES.has(b.status) ? '#22c55e'
                    : UPCOMING_STATUSES.has(b.status) ? 'var(--orange-9)'
                    : CANCELLED_STATUSES.has(b.status) ? '#e5484d'
                    : 'var(--gray-9)';
                  return (
                    <li
                      key={b.id || i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 0',
                        borderBottom: i < recentActivity.length - 1 ? '1px solid var(--gray-4)' : 'none',
                      }}
                    >
                      {provider?.photoUrl ? (
                        <img
                          src={provider.photoUrl}
                          alt=""
                          style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--orange-3)',
                            color: 'var(--orange-10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {(provider?.businessName || 'S').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', fontWeight: 600, color: 'var(--gray-12)', textAlign: 'left' }}>
                          {resolveServiceName(b)}
                          {provider?.businessName && (
                            <span style={{ color: 'var(--gray-10)', fontWeight: 400 }}>
                              {' '}· {provider.businessName}
                            </span>
                          )}
                        </div>
                        <div className="text-wrap-safe" style={{ fontSize: 'var(--font-size-caption)', color: 'var(--gray-10)', textAlign: 'left' }}>
                          {d ? format(d, 'MMM d, yyyy · p') : '—'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0 sm:flex-row sm:items-center sm:gap-2">
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            color: color,
                            backgroundColor: `${color}20`,
                          }}
                        >
                          {b.status}
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-12)' }}>
                          {bookingAmount(b) > 0 ? formatCurrency(bookingAmount(b)) : ''}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        {/* Review highlights */}
        {reviews.length > 0 && (
          <Card style={{ marginTop: '16px' }}>
            <SectionTitle icon={StarIcon}>Recent Reviews You've Left</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reviews.slice(0, 3).map((r) => (
                <div
                  key={r.id}
                  style={{
                    padding: '14px',
                    border: '1px solid var(--gray-4)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--gray-2)',
                  }}
                >
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarSolid
                        key={i}
                        style={{
                          width: '14px',
                          height: '14px',
                          color: i < (r.rating || 0) ? '#facc15' : 'var(--gray-5)',
                        }}
                      />
                    ))}
                  </div>
                  {r.comment && (
                    <p className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', color: 'var(--gray-11)', margin: 0, textAlign: 'left' }}>
                      "{r.comment.length > 120 ? `${r.comment.slice(0, 120).trim()}…` : r.comment}"
                    </p>
                  )}
                  {providerMap[r.providerId]?.businessName && (
                    <div
                      className="flex items-center gap-2"
                      style={{ marginTop: '10px', fontSize: '12px', color: 'var(--gray-10)' }}
                    >
                      <UserCircleIcon style={{ width: '14px', height: '14px' }} />
                      {providerMap[r.providerId].businessName}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
